import React, { useMemo, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useBlogs } from "../../services/blogService";
import { useSelector } from "react-redux";
import type { RootState } from "../../store";

interface BlogPost {
  id: string;
  title: string;
  description: string;
  image: string;
  date: string;
  author?: string;
  category?: string;
  readTime?: number;
}

interface BlogAndNewsProps {
  title?: string;
  subtitle?: string;
  rssLink?: string;
  posts?: BlogPost[];
  showViewAll?: boolean;
  onViewAll?: () => void;
  onPostClick?: (post: BlogPost) => void;
  onReadMore?: (post: BlogPost) => void;
}

const BlogAndNews: React.FC<BlogAndNewsProps> = ({
  title,
  subtitle,
  rssLink = "rss.xml",
  posts,
  showViewAll = true,
  onViewAll,
  onPostClick,
  onReadMore,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const [isLoaded, setIsLoaded] = useState(false);

  // Fetch blogs from API
  const { data: blogsData } = useBlogs({
    locale: currentLanguage,
    status: "published",
    per_page: posts?.length || 6,
  });

  // Transform API posts to match component interface
  const apiPosts = useMemo(() => {
    if (posts || !blogsData?.posts) return []; // Return empty if posts prop is provided
    return blogsData.posts.slice(0, 6).map((post) => ({
      id: post.id,
      title: post.title,
      description: post.excerpt || "",
      image: post.featured_image || "",
      date: post.published_at
        ? new Date(post.published_at).toLocaleDateString(currentLanguage, {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
        : "",
      author: post.author || t("blog.authors.community", "Community Team"),
      category: post.categories && post.categories.length > 0 ? post.categories[0] : t("blog.categories.general", "General"),
      readTime: post.read_time || 5,
    }));
  }, [blogsData, currentLanguage, t, posts]);

  const displayPosts = posts || apiPosts;

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-white via-stone-50/50 to-emerald-50/50 dark:from-gray-800 dark:via-stone-900/10 dark:to-gray-900">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-stone-200/20 dark:bg-stone-900/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-200/20 dark:bg-emerald-900/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={
              isLoaded ? { scale: 1, opacity: 1 } : { scale: 0.9, opacity: 0 }
            }
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-block mb-6"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-stone-800 dark:text-stone-300 rounded-full text-sm font-semibold border border-stone-200 dark:border-stone-800 shadow-lg">
              <span className="text-lg">📰</span>
              <span>{t("blog.badge", "Community Stories")}</span>
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 dark:text-white mb-6 leading-tight tracking-tight"
          >
            {title || t("blog.title", "Blog & News")}
            <span className="block mt-2 bg-gradient-to-r from-stone-600 via-emerald-600 to-stone-700 bg-clip-text text-transparent">
              {t("blog.titleAccent", "Stay Connected")}
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto mb-8"
          >
            {subtitle ||
              t(
                "blog.subtitle",
                "Stay updated with our latest stories, updates, and community insights."
              )}
          </motion.p>
          <motion.a
            href={rssLink}
            initial={{ opacity: 0, y: 20 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-stone-500 to-emerald-600 hover:from-stone-600 hover:to-emerald-700 text-white rounded-xl font-bold transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3.5 3a.5.5 0 00-.5.5v.5c0 1.5.5 3 1.5 4.5A5.5 5.5 0 003.5 8c0-1.5.5-3 1.5-4.5V3.5a.5.5 0 00-.5-.5z" />
              <path d="M14 3.5c0-.28-.22-.5-.5-.5h-9a.5.5 0 00-.5.5V5c0 4.5 3 8.5 7 9.5a.5.5 0 00.5-.5V3.5z" />
            </svg>
            {t("blog.rssFeed", "Subscribe to RSS Feed")}
          </motion.a>
        </motion.div>

        {/* Blog Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 lg:gap-10">
          {displayPosts.map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 50 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              whileHover={{ y: -12, scale: 1.03 }}
              className="group relative"
            >
              {/* Floating gradient glow */}
              <div className="absolute -inset-1 bg-gradient-to-r from-stone-500 via-emerald-500 to-stone-600 rounded-3xl opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-500"></div>

              {/* Main Card */}
              <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 overflow-hidden h-full">
                {/* Image Section */}
                <div className="relative h-64 overflow-hidden">
                  {post.image ? (
                    <>
                      <div
                        className="w-full h-full bg-center bg-no-repeat bg-cover transition-transform duration-500 group-hover:scale-110"
                        style={{ backgroundImage: `url('${post.image}')` }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                    </>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center">
                      <span className="text-gray-400 text-sm">No image</span>
                    </div>
                  )}

                  {/* Category Badge */}
                  {post.category && (
                    <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-white/20 backdrop-blur-md text-white border border-white/30">
                        {post.category}
                      </span>
                    </div>
                  )}

                  {/* Read Time Badge */}
                  {post.readTime && (
                    <div className="absolute top-4 right-4">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-black/20 backdrop-blur-md text-white border border-white/20">
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {post.readTime} {t("blog.minRead", "min read")}
                      </span>
                    </div>
                  )}
                </div>

                {/* Content Section */}
                <div className="p-6 lg:p-8">
                  <h3 className="text-xl lg:text-2xl font-extrabold text-gray-900 dark:text-white mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-stone-600 group-hover:to-emerald-600 transition-all duration-300 line-clamp-2 leading-tight">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm lg:text-base leading-relaxed mb-6 line-clamp-3">
                    {post.description}
                  </p>

                  {/* Meta Information */}
                  <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-6 pb-6 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="font-semibold">{post.date}</span>
                    </div>
                    {post.author && (
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="font-semibold">{post.author}</span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        if (onPostClick) {
                          onPostClick(post);
                        } else {
                          navigate(`/${currentLanguage}/blogs/${post.id}`);
                        }
                      }}
                      className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-xl font-bold transition-all duration-200 text-sm"
                    >
                      {t("blog.readMore", "Read More")}
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => {
                        if (onReadMore) {
                          onReadMore(post);
                        } else {
                          navigate(`/${currentLanguage}/blogs/${post.id}`);
                        }
                      }}
                      className="px-4 py-3 bg-gradient-to-r from-stone-500 to-emerald-600 hover:from-stone-600 hover:to-emerald-700 text-white rounded-xl font-bold transition-all duration-200 text-sm shadow-lg hover:shadow-xl"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                        />
                      </svg>
                    </motion.button>
                  </div>
                </div>

                {/* Decorative gradient corner */}
                <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-gradient-to-br from-stone-500 to-emerald-600 opacity-10 group-hover:opacity-20 rounded-full blur-2xl transition-opacity duration-500"></div>
              </div>
            </motion.article>
          ))}
        </div>

        {/* View All Button */}
        {showViewAll && displayPosts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="text-center mt-20"
          >
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                if (onViewAll) {
                  onViewAll();
                } else {
                  navigate("/blogs");
                }
              }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-800 text-gray-900 dark:text-white rounded-2xl font-bold text-lg transition-all duration-200 shadow-xl hover:shadow-2xl border-2 border-gray-200 dark:border-gray-700"
            >
              {t("blog.viewAll", "View All Posts")}
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </motion.button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default BlogAndNews;
